import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { AUTH_SOURCE_PARAM_KEY } from '~/modules/root/hook/auth-event-params';

/**
 * Whether this document load is the far side of a social round-trip that the
 * named gate started.
 *
 * Captured during the first render rather than in an effect: `useAuthEvent`
 * strips these params from the URL as soon as it runs, so an effect reading
 * `window.location` can arrive after the evidence is gone.
 */
const startedBy = (authSource: string): boolean => {
    if (typeof window === 'undefined') return false;
    return (
        new URLSearchParams(window.location.search).get(
            AUTH_SOURCE_PARAM_KEY,
        ) === authSource
    );
};

interface UseGateNavigationResetOptions {
    /** Whether the gate is currently showing. */
    isOpen: boolean;
    /** Names this gate on the params a social redirect appends. */
    authSource: string;
    /** Clears the open flag and the intent together. */
    close: () => void;
}

/**
 * Closes an auth gate that a navigation has left behind.
 *
 * The gates persist their open flag in sessionStorage so the modal is still
 * there when a social round-trip's full reload lands back on the page. That
 * makes every other new document restore it too: a gate opened on one
 * professional's downloads reappeared on the next professional's page, and on
 * the homepage, still holding the first professional's file and id — so the
 * wrong client would have been credited with the lead.
 *
 * The round-trip is the one navigation a gate may survive, and it says so on
 * the URL. Anything else — a reader who left the modal rather than dismissing
 * it, a reload, a client-side route change — closes it.
 *
 * Mount once per gate, beside the modal that owns it.
 */
export const useGateNavigationReset = ({
    isOpen,
    authSource,
    close,
}: UseGateNavigationResetOptions): void => {
    const { pathname } = useLocation();

    const closeRef = useRef(close);
    closeRef.current = close;

    const resumingRef = useRef(startedBy(authSource));
    /** The restore decision is made once per document, on the first commit. */
    const settledRef = useRef(false);
    /** The route the gate is entitled to stay open on. */
    const heldPathRef = useRef(pathname);

    useEffect(() => {
        if (settledRef.current) return;
        settledRef.current = true;
        heldPathRef.current = pathname;
        if (!isOpen || resumingRef.current) return;
        closeRef.current();
        // Deliberately keyed on nothing that changes later: this is the single
        // decision about what the page was loaded holding.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (pathname === heldPathRef.current) return;
        heldPathRef.current = pathname;
        // A gate the reader navigated away from is one they walked away from,
        // whether or not they found the close button.
        closeRef.current();
    }, [pathname]);
};
